import React from 'react';
import classNames from 'classnames/bind';

import ServiceForm from '@/components/CustomTravel/ServiceForm';
import { DimmedContent, DimmedFooter, DimmedHeader } from '@/components/CustomTravel/DimmedPopup';
import { CustomTravelEstimatePopupStep, CustomTravelParamType } from '@/components/CustomTravel/EstimatePopup';
import RadioField from '@/components/CustomTravel/ServiceFormFields/RadioField';
import ConditionalField from '@/components/CustomTravel/ServiceFormFields/ConditionalField';
import InputNumberWithCommaField from '@/components/CustomTravel/ServiceFormFields/InputNumberWithCommaField';
import { CustomTravelBudget, BudgetType, BudgetFlexibility } from '@/atoms/customTravelFormState';
import { parseNumberFormCommas, formatNumberWithCommas } from '@pkgtour-workspace/common/dist/util/formatter';
import usePopupHandler from '@/hooks/custom/usePopupHandler';
import RecommendBudget from './RecommendBudget';

import styles from './SelectBudgetForm.module.scss';

const cx = classNames.bind(styles);

interface SelectBudgetFormProps {
  externalValues?: { budget: CustomTravelBudget };
}

const SelectBudgetForm: React.FC<SelectBudgetFormProps> = ({ externalValues: _externalValues }) => {
  const externalValues = _externalValues
    ? {
        ..._externalValues,
        budget: { ..._externalValues.budget, amount: formatNumberWithCommas(_externalValues.budget.amount) },
      }
    : undefined;

  const { handleBack, onSubmitConfirm, currentType } = usePopupHandler();

  const handleSubmit = (_values: any) => {
    const budgetAmount = parseNumberFormCommas(_values?.budget?.amount?.toString() || '');

    if (isNaN(budgetAmount)) {
      return;
    }
    const values = { budget: { ..._values?.budget, amount: budgetAmount } };
    onSubmitConfirm(values);
  };

  return (
    <ServiceForm onSubmit={handleSubmit} className={cx('SelectBudget')} externalValues={externalValues}>
      <DimmedHeader>
        {currentType !== CustomTravelParamType.CHECK_ESTIMATE && (
          <span className={cx('page')} data-event-nlog="imp" data-event-area-code="customtravel.popup.budget">
            <span className={cx('blind')}>현재</span>
            <b>{'5'}</b>
            <span className={cx('blind')}>전체</span>
            <span>{'5'}</span>
          </span>
        )}
        <span className={cx('title')}>여행 예산</span>
      </DimmedHeader>
      <DimmedContent>
        <div className={cx('type')}>
          <div className={cx('radios')}>
            <RadioField
              name="budget.budgetType"
              displayText="성인 1인당 예산"
              className={cx('radio')}
              value={BudgetType.PER_PERSON}
              defaultValue={BudgetType.PER_PERSON}
              nlogAreaCode="cus.selfbudgetperperson"
            />
            <RadioField
              name="budget.budgetType"
              displayText="여행 총 예산"
              className={cx('radio')}
              value={BudgetType.TOTAL}
              nlogAreaCode="cus.selfbudgettotal"
            />
          </div>
          <div className={cx('amount')}>
            <ConditionalField targetFieldName="budget.budgetType">
              {(budgetType) => (
                <InputNumberWithCommaField
                  name="budget.amount"
                  placeholder={budgetType === BudgetType.PER_PERSON ? '성인 1인당 금액' : '여행 총 금액'}
                  required
                  minLength={0}
                  maxLength={7}
                />
              )}
            </ConditionalField>
            <span className={cx('unit')}>만 원</span>
          </div>
        </div>
        <div className={cx('compromise')}>
          <RadioField
            name="budget.flexibility"
            displayText="살짝 초과해도 괜찮아요."
            className={cx('radio')}
            value={BudgetFlexibility.FLEXIBLE}
            defaultValue={BudgetFlexibility.FLEXIBLE}
            nlogAreaCode="cus.selfbudgetflexible"
          />
          <RadioField
            name="budget.flexibility"
            displayText="최대한 맞춰주세요."
            className={cx('radio')}
            value={BudgetFlexibility.FIXED}
            nlogAreaCode="cus.selfbudgetfixed"
          />
          <RadioField
            name="budget.flexibility"
            displayText="저렴할수록 좋아요."
            className={cx('radio')}
            value={BudgetFlexibility.ECONOMICAL}
            nlogAreaCode="cus.selfbudgeteconomial"
          />
        </div>
        <RecommendBudget />
      </DimmedContent>
      <DimmedFooter>
        <div className={cx('progress')}>
          <button
            type="button"
            className={cx('prev')}
            onClick={handleBack(CustomTravelEstimatePopupStep.SelectPassenger)}
            data-event-nlog="click"
            data-event-area-code="cus.selfbudgetnackpage"
          >
            {currentType !== CustomTravelParamType.CHECK_ESTIMATE ? '이전' : '취소'}
          </button>
          <ConditionalField<string> targetFieldName="budget.amount">
            {(budgetAmount) => (
              <button
                type="submit"
                className={cx('next')}
                disabled={!budgetAmount || budgetAmount === '0'}
                data-event-nlog="click"
                data-event-area-code="cus.selfbudgetnextpage"
              >
                {currentType !== CustomTravelParamType.CHECK_ESTIMATE ? '다음' : '수정하기'}
              </button>
            )}
          </ConditionalField>
        </div>
      </DimmedFooter>
    </ServiceForm>
  );
};

export default SelectBudgetForm;
